const ROOT_DOMAIN = process.env.REACT_APP_ROOT_DOMAIN || "";

export function normalizeHost(host) {
  if (!host || typeof host !== "string") return "";
  return host.split(",")[0].trim().toLowerCase().split(":")[0];
}

export function getHostParts(host) {
  const normalized = normalizeHost(host);
  return normalized ? normalized.split(".") : [];
}

export function getRootDomain(host) {
  const normalized = normalizeHost(host);
  if (!normalized) return "";

  if (
    normalized === "localhost" ||
    normalized.endsWith(".localhost") ||
    /^\d{1,3}(\.\d{1,3}){3}$/.test(normalized)
  ) {
    return "localhost";
  }

  if (ROOT_DOMAIN && normalized.endsWith(ROOT_DOMAIN.toLowerCase())) {
    return ROOT_DOMAIN.toLowerCase();
  }

  const parts = normalized.split(".");
  if (parts.length <= 2) {
    return normalized;
  }

  return parts.slice(-2).join(".");
}

export function getSubdomainPrefix(host) {
  // 1. Check URL query parameters first (?subdomain=... or ?prefix=...)
  if (typeof window !== "undefined" && window.location && window.location.search) {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const querySub = urlParams.get("subdomain") || urlParams.get("prefix");
      if (querySub && querySub.trim()) {
        return querySub.trim().toLowerCase();
      }
    } catch (e) {
      // ignore
    }
  }

  const normalized = normalizeHost(
    host || (typeof window !== "undefined" ? window.location.hostname : "")
  );
  if (!normalized) return null;

  // Plain localhost or raw IP has no subdomain
  if (normalized === "localhost" || /^\d{1,3}(\.\d{1,3}){3}$/.test(normalized)) {
    return null;
  }

  // Handle *.localhost (e.g. kf.localhost or demo.localhost)
  if (normalized.endsWith(".localhost")) {
    const sub = normalized.replace(/\.localhost$/, "");
    if (sub && sub !== "www") {
      return sub;
    }
    return null;
  }

  const rootDomain = getRootDomain(normalized);
  if (!rootDomain || normalized === rootDomain || normalized === `www.${rootDomain}`) {
    return null;
  }

  const hostParts = normalized.split(".");
  const rootParts = rootDomain.split(".");
  if (hostParts.length > rootParts.length) {
    const sub = hostParts.slice(0, hostParts.length - rootParts.length).join(".");
    if (sub && sub !== "www") {
      return sub;
    }
  }

  // Fallback: if hostname has at least 3 segments (e.g. demo.something.com)
  if (hostParts.length >= 3 && hostParts[0] !== "www") {
    return hostParts[0];
  }

  return null;
}

export function isRootDomain(host) {
  const normalized = normalizeHost(
    host || (typeof window !== "undefined" ? window.location.hostname : "")
  );
  const rootDomain = getRootDomain(normalized);
  return (
    !normalized ||
    normalized === "localhost" ||
    normalized === rootDomain ||
    normalized === `www.${rootDomain}` ||
    /^\d{1,3}(\.\d{1,3}){3}$/.test(normalized)
  );
}

export function buildTenantLoginUrl(prefix, host) {
  const currentHost = normalizeHost(
    host || (typeof window !== "undefined" ? window.location.hostname : "")
  );
  if (!prefix) return null;
  const normalizedPrefix = prefix.trim().toLowerCase();
  if (!normalizedPrefix) return null;
  const protocol =
    typeof window !== "undefined" && window.location.protocol
      ? window.location.protocol
      : "http:";
  const port =
    typeof window !== "undefined" && window.location.port
      ? `:${window.location.port}`
      : "";

  // If on localhost, use prefix.localhost:port
  if (currentHost === "localhost" || currentHost.endsWith(".localhost")) {
    return `${protocol}//${normalizedPrefix}.localhost${port}/login`;
  }

  const rootDomain = getRootDomain(currentHost);
  if (!rootDomain) return null;
  return `${protocol}//${normalizedPrefix}.${rootDomain}${port}/login`;
}
